import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { ChevronLeftIcon, PlusIcon, QuestionMark2Icon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Navigate, useParams } from 'react-router-dom'

import { createLanguageTestQuestion } from '../../api/language-test/create-language-test-question'
import { getLanguageTestSection } from '../../api/language-test/get-language-test-section'
import { getLanguageTestTemplate } from '../../api/language-test/get-language-test-template'
import UserNavBar from '../../components/elements/nav-bar'
import LanguageTestHeader from '../../components/pages/language-test/language-test-header'
import LanguageTestQuestion from '../../components/pages/language-test/language-test-question'
import LanguageTestQuestionCreationForm from '../../components/pages/language-test/language-test-question-creation-form'
import { Paths } from '../../lib/constants/path'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { LanguageTestQuestionInput } from '../../lib/types/models/language-test/language-test-question'
import { queryClient } from '../../main'

const LanguageTestSectionDetails = () => {
  const { sectionId, templateId } = useParams<{ templateId: string; sectionId: string }>()
  const { navigate } = useNavigator()

  const { toggleModal, modalState, closeModel } = useModalManager<
    'language-test-question-creation',
    LanguageTestQuestionInput
  >(['language-test-question-creation'])

  if (!templateId || !sectionId) return <Navigate to={Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE} />

  const { data: languageTestSection, isLoading: isLoadingSection } = useQuery({
    queryKey: ['language-test-section', sectionId],
    queryFn: () => getLanguageTestSection(parseInt(sectionId)),
    enabled: !!sectionId && !isNaN(parseInt(sectionId))
  })

  const { data: languageTestTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['language-test-template', templateId],
    queryFn: () => getLanguageTestTemplate(parseInt(templateId)),
    enabled: !!templateId && !isNaN(parseInt(templateId))
  })

  const { mutate: createQuestion, isPending: isCreatingQuestion } = useMutation({
    mutationFn: createLanguageTestQuestion,
    onSuccess: () => {
      closeModel('language-test-question-creation')
      queryClient.invalidateQueries({ queryKey: ['language-test-section', sectionId] })
    }
  })

  const navigateBackToTemplate = () =>
    navigate(Paths.LANGUAGE_TEST_TEMPLATE_DETAILS_PAGE.replace(':id', templateId))

  return (
    <div>
      <UserNavBar
        title={'Détails du test de langue'}
        icon={ChevronLeftIcon}
        onIconClick={navigateBackToTemplate}
      />
      <div className="h-4" />

      <Box>
        <div className="flex flex-col gap-4">
          <div>
            {isLoadingTemplate ? (
              <Skeleton className="h-[73px] w-full" />
            ) : languageTestTemplate ? (
              <LanguageTestHeader
                template={languageTestTemplate}
                onDeleteSuccess={() => {
                  navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)
                }}
              />
            ) : (
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Section non trouvée
              </Typography>
            )}
          </div>

          <div className="flex items-center justify-between">
            {isLoadingSection ? (
              <Skeleton className="h-8 w-80" />
            ) : (
              <Typography variant="h5">
                Questions de la section - {languageTestSection?.label}
              </Typography>
            )}

            <Button
              icon={PlusIcon}
              label="Ajouter une question"
              onClick={() => toggleModal('language-test-question-creation')}
              disabled={isLoadingSection}
            />
          </div>

          <div className="flex flex-col gap-4">
            {isLoadingSection ? (
              Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-32 w-full" />
              ))
            ) : languageTestSection?.questions && languageTestSection.questions.length > 0 ? (
              languageTestSection.questions
                .sort((a, b) => {
                  if (a.position === b.position) return a.id - b.id
                  return a.position - b.position
                })
                .map((question, index) => (
                  <LanguageTestQuestion key={question.id} question={question} index={index} />
                ))
            ) : (
              <div className="flex items-center justify-center p-4">
                <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                  Aucune question trouvée
                </Typography>
              </div>
            )}
          </div>
        </div>
      </Box>

      <Modal
        icon={QuestionMark2Icon}
        isOpen={modalState['language-test-question-creation']}
        onCancel={() => closeModel('language-test-question-creation')}
        title="Ajouter une question"
        hideActions
      >
        <LanguageTestQuestionCreationForm
          className={MODAL_BODY_CLASSNAME}
          isLoading={isCreatingQuestion}
          onSubmit={(data) => {
            createQuestion({ sectionId: parseInt(sectionId), ...data })
          }}
          onCancel={() => closeModel('language-test-question-creation')}
        />
      </Modal>
    </div>
  )
}

export default LanguageTestSectionDetails
